import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { useSSERefetch } from "../hooks/useSSERefetch";
import { useUrlState } from "../hooks/useUrlState";
import StatusBadge from "../components/StatusBadge";
import PlatformBadge from "../components/PlatformBadge";
import Pagination from "../components/Pagination";

interface TestRunRaw {
  id: string;
  jobId: string;
  pipelineId: string;
  mergeRequestIid: string | null;
  appName: string;
  platform: "android" | "ios" | "web";
  passedCount: number;
  failedCount: number;
  skippedCount: number;
  totalScenarios: number;
  startedAt: string | null;
  status: string;
  ciJobName?: string | null;
  ciJobUrl?: string | null;
  ciPipelineUrl?: string | null;
  mrTitle?: string | null;
  mrUrl?: string | null;
  branch?: string | null;
  pipelineSource?: string | null;
}

interface TestRun {
  id: string;
  jobId: string;
  mrLink: string | null;
  mergeRequestIid: string | null;
  app: string;
  platform: "android" | "ios" | "web";
  passCount: number;
  failCount: number;
  skipCount: number;
  totalCount: number;
  startedAt: string | null;
  status: string;
  ciJobName?: string | null;
  ciJobUrl?: string | null;
  ciPipelineUrl?: string | null;
  mrTitle?: string | null;
  mrUrl?: string | null;
  branch?: string | null;
  pipelineSource?: string | null;
}

interface RunsResponse {
  runs: TestRunRaw[];
  total: number;
}

function mapRun(r: TestRunRaw): TestRun {
  return {
    id: r.id,
    jobId: r.jobId,
    mrLink: r.mergeRequestIid,
    mergeRequestIid: r.mergeRequestIid,
    app: r.appName,
    platform: r.platform,
    passCount: r.passedCount,
    failCount: r.failedCount,
    skipCount: r.skippedCount,
    totalCount: r.totalScenarios,
    startedAt: r.startedAt,
    status: r.status,
    ciJobName: r.ciJobName,
    ciJobUrl: r.ciJobUrl,
    ciPipelineUrl: r.ciPipelineUrl,
    mrTitle: r.mrTitle,
    mrUrl: r.mrUrl,
    branch: r.branch,
    pipelineSource: r.pipelineSource,
  };
}


function SourceChip({ run }: { run: TestRun }) {
  if (run.pipelineSource === "merge_request_event" && run.mrUrl) {
    const title = run.mrTitle ?? "MR";
    const truncated = title.length > 32 ? title.slice(0, 32) + "…" : title;
    return (
      <a
        href={run.mrUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "inline-block",
          marginTop: "2px",
          padding: "1px 6px",
          borderRadius: "var(--radius-sm)",
          fontSize: "var(--font-size-sm)",
          background: "rgba(59, 130, 246, 0.12)",
          color: "var(--color-info)",
          textDecoration: "none",
          whiteSpace: "nowrap",
          maxWidth: "220px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        !{run.mergeRequestIid} {truncated}
      </a>
    );
  }
  if (run.branch) {
    return (
      <span
        style={{
          display: "inline-block",
          marginTop: "2px",
          padding: "1px 6px",
          borderRadius: "var(--radius-sm)",
          fontSize: "var(--font-size-sm)",
          background: "var(--color-foreground)",
          color: "var(--color-text-secondary)",
          whiteSpace: "nowrap",
          maxWidth: "220px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        mainline · {run.branch}
      </span>
    );
  }
  return null;
}

function FilterChip<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: [T, string][];
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-xs)" }}>
      <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
        {label}:
      </span>
      <div
        style={{
          display: "flex",
          gap: 2,
          border: "var(--border-default)",
          borderRadius: "var(--radius-sm)",
          overflow: "hidden",
          background: "var(--color-surface)",
        }}
      >
        {options.map(([v, l]) => (
          <button
            key={v}
            onClick={() => onChange(v)}
            style={{
              padding: "var(--space-xs) var(--space-sm)",
              fontSize: "var(--font-size-sm)",
              border: "none",
              background: value === v ? "var(--color-info)" : "var(--color-surface)",
              color: value === v ? "var(--color-surface)" : "var(--color-text-primary)",
              cursor: "pointer",
              fontWeight: value === v ? 600 : 400,
              transition: "background 0.15s, color 0.15s",
            }}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

const PAGE_SIZE = 25;

export default function TestRunsList() {
  const { state, setState } = useUrlState({
    page: 1,
    app: "",
    status: "",
    platform: "all" as "all" | "mobile" | "web",
    source: "all" as "all" | "mr" | "mainline",
  });

  const params = new URLSearchParams();
  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String((state.page - 1) * PAGE_SIZE));
  if (state.app) params.set("app", state.app);
  if (state.status) params.set("status", state.status);
  if (state.platform && state.platform !== "all") params.set("platform", state.platform);
  if (state.source && state.source !== "all") params.set("source", state.source);
  const url = `/api/runs?${params.toString()}`;

  const { data, loading, error, refetch } = useApi<RunsResponse>(url);
  useSSERefetch("/api/events", [
    "run:started",     // new run created
    "run:execution",   // scenario completed → counts change
    "run:completed",   // run finalized
  ], refetch);

  // Clamp out-of-range pages once data arrives. Handles /runs?page=999
  // and the case where a filter shrinks the result set below the
  // current page.
  useEffect(() => {
    if (!data) return;
    const maxPage = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
    if (state.page > maxPage) setState({ page: maxPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const runs = useMemo(() => (data?.runs ?? []).map(mapRun), [data]);
  const navigate = useNavigate();

  const apps = useMemo(() => {
    return Array.from(new Set(runs.map((r) => r.app))).sort();
  }, [runs]);

  const hasActiveFilters =
    !!state.app ||
    !!state.status ||
    state.platform !== "all" ||
    state.source !== "all";

  if (loading && !data) {
    return <p style={{ padding: "var(--space-xl)" }}>Loading test runs...</p>;
  }

  if (error) {
    return (
      <p style={{ padding: "var(--space-xl)", color: "var(--color-error)" }}>
        Error: {error}
      </p>
    );
  }

  return (
    <div>
      <h1 style={pageTitleStyle}>Test Runs</h1>

      {/* Filters */}
      <section style={filterBarStyle}>
        <select
          aria-label="Filter by app"
          value={state.app}
          onChange={(e) => setState({ app: e.target.value })}
          style={filterInputStyle}
        >
          <option value="">All Apps</option>
          {apps.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>

        <select
          aria-label="Filter by status"
          value={state.status}
          onChange={(e) => setState({ status: e.target.value })}
          style={filterInputStyle}
        >
          <option value="">All Statuses</option>
          <option value="passed">Passed</option>
          <option value="failed">Failed</option>
          <option value="running">Running</option>
        </select>

        <FilterChip
          label="Platform"
          value={state.platform}
          onChange={(v) => setState({ platform: v })}
          options={[["all", "All"], ["mobile", "Mobile"], ["web", "Web"]] as ["all" | "mobile" | "web", string][]}
        />

        <FilterChip
          label="Source"
          value={state.source}
          onChange={(v) => setState({ source: v })}
          options={[["all", "All"], ["mr", "MR"], ["mainline", "Mainline"]] as ["all" | "mr" | "mainline", string][]}
        />
      </section>

      {/* Table */}
      <div
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-md)",
          boxShadow: "var(--shadow-card)",
          overflow: "auto",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--color-foreground)" }}>
              {["Platform", "Job", "MR", "App", "Pass", "Fail", "Skip", "Total", "Status", "Started"].map(
                (h) => (
                  <th key={h} style={thStyle}>
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr
                key={run.id}
                onClick={() => navigate(`/runs/${run.id}`)}
                style={{
                  cursor: "pointer",
                  borderBottom: "var(--border-default)",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLElement).style.background = "var(--color-foreground)")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLElement).style.background = "transparent")
                }
              >
                <td style={tdStyle}>
                  <PlatformBadge platform={run.platform} />
                </td>
                <td style={{ ...tdStyle, fontFamily: "var(--font-mono)", fontSize: "var(--font-size-sm)" }}>
                  {run.ciJobUrl ? (
                    <a
                      href={run.ciJobUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: "var(--color-text-primary)", textDecoration: "underline" }}
                    >
                      {run.ciJobName ?? run.jobId}
                    </a>
                  ) : (
                    <span>{run.ciJobName ?? run.jobId}</span>
                  )}
                  <div>
                    <SourceChip run={run} />
                  </div>
                </td>
                <td style={tdStyle}>
                  {run.mrLink ? (
                    <span style={{ color: "var(--color-info)" }}>!{run.mrLink}</span>
                  ) : (
                    <span style={{ color: "var(--color-text-secondary)" }}>—</span>
                  )}
                </td>
                <td style={tdStyle}>{run.app}</td>
                <td style={{ ...tdStyle, color: "var(--color-success)", fontWeight: 600 }}>{run.passCount}</td>
                <td style={{ ...tdStyle, color: run.failCount > 0 ? "var(--color-danger)" : "inherit", fontWeight: 600 }}>
                  {run.failCount}
                </td>
                <td style={{ ...tdStyle, color: run.skipCount > 0 ? "var(--color-warning)" : "var(--color-text-secondary)", fontWeight: 600 }}>
                  {run.skipCount}
                </td>
                <td style={tdStyle}>{run.totalCount}</td>
                <td style={tdStyle}>
                  <StatusBadge status={run.status} />
                </td>
                <td style={{ ...tdStyle, color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)" }}>
                  {run.startedAt ? new Date(run.startedAt).toLocaleString() : "—"}
                </td>
              </tr>
            ))}
            {runs.length === 0 && (
              <tr>
                <td colSpan={10} style={{ ...tdStyle, color: "var(--color-text-secondary)", textAlign: "center" }}>
                  {hasActiveFilters ? "No runs match the current filters." : "No test runs yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data && (
        <Pagination
          page={state.page}
          pageSize={PAGE_SIZE}
          total={data.total}
          onPageChange={(p) => setState({ page: p })}
        />
      )}
    </div>
  );
}

const pageTitleStyle: React.CSSProperties = {
  fontSize: "var(--font-size-xl)",
  fontWeight: 700,
  marginBottom: "var(--space-xl)",
  color: "var(--color-primary)",
};

const filterBarStyle: React.CSSProperties = {
  display: "flex",
  gap: "var(--space-md)",
  marginBottom: "var(--space-xl)",
  flexWrap: "wrap",
  alignItems: "center",
};

const filterInputStyle: React.CSSProperties = {
  fontSize: "var(--font-size-base)",
  padding: "var(--space-sm) var(--space-md)",
  border: "var(--border-default)",
  borderRadius: "var(--radius-sm)",
  background: "var(--color-surface)",
  color: "var(--color-text-primary)",
  minHeight: "var(--touch-target-min)",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "var(--space-sm) var(--space-md)",
  fontSize: "var(--font-size-sm)",
  fontWeight: 600,
  color: "var(--color-text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const tdStyle: React.CSSProperties = {
  padding: "var(--space-sm) var(--space-md)",
  fontSize: "var(--font-size-base)",
  verticalAlign: "middle",
};
